import {
  setBrandBackendRequirementsData,
  setBrandConfigurationPIEChartsData,
  setBrandConfigurationReportList,
  setBrandPropertiesData,
} from "../../../features/brandConfigurationReportSlice";
import { setDisplayErrorSnackBar } from "../../../features/userSlice";
import { brandConfigReportService } from "../../../services/api/brand-config-report.endpoints";
import store from "../../../store";

async function getBrandConfigurationReport(oauthTokenHeader: string) {
  try {
    const response =
      await brandConfigReportService.getBrandConfigurationReport(
        oauthTokenHeader,
      );
    if (!response || !response.status) {
      throw new Error("Failed to access brand configuration report.");
    } else if (response.status === 200) {
      const brandConfigReports = response?.data?.data ?? [];
      store.dispatch(setBrandConfigurationReportList(brandConfigReports)); // Set the fetched data
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText || "Failed to access brand configuration report.",
        type: "error",
      }),
    );
  }
}

async function getBrandConfigPIECharts(
  oauthTokenHeader: string,
  website_shared_uid: string,
) {
  try {
    const response = await brandConfigReportService.getBrandConfigPIECharts(
      oauthTokenHeader,
      website_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand configuration PIE charts.");
    } else if (response.status === 200) {
      const pieCharts = response?.data?.data?.query_output ?? [];
      store.dispatch(setBrandConfigurationPIEChartsData(pieCharts)); // Set the fetched data
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText || "Failed to access brand configuration PIE charts.",
        type: "error",
      }),
    );
  }
}

async function getBrandBackendRequirements(
  oauthTokenHeader: string,
  backend_shared_uid: string,
) {
  try {
    const response = await brandConfigReportService.getBrandBackendRequirements(
      oauthTokenHeader,
      backend_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error(
        "Failed to access brand configuration backend requirements.",
      );
    } else if (response.status === 200) {
      const backendRequirements = response?.data?.data ?? [];
      store.dispatch(setBrandBackendRequirementsData(backendRequirements)); // Set the fetched data
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText ||
          "Failed to access brand configuration backend requirements.",
        type: "error",
      }),
    );
  }
}

async function getBrandProperties(
  oauthTokenHeader: string,
  brand_shared_uid: string,
) {
  try {
    const response = await brandConfigReportService.getBrandProperties(
      oauthTokenHeader,
      brand_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand properties.");
    } else if (response.status === 200) {
      const brandProperties = response?.data?.data ?? [];
      store.dispatch(setBrandPropertiesData(brandProperties)); // Set the fetched data
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access brand properties.",
        type: "error",
      }),
    );
  }
}

async function updateBrandProperties(
  oauthTokenHeader: string,
  data: any,
  brand_shared_uid: any,
) {
  try {
    const response = await brandConfigReportService.editBrandProperties(
      oauthTokenHeader,
      data,
      brand_shared_uid,
    );

    if (!response) {
      throw new Error("Failed to update brand properties.");
    }

    // Handle successful response codes
    if (response.status === 200) {
      //return response?.data;
      return response;
    }

    if (response.status === 207) {
      // Extract error message from response if available
      const errorMessage =
        "Failed to update brand properties. Please try again.";
      throw new Error(errorMessage);
    }

    // Handle client errors (400)
    if (response.status === 400) {
      throw new Error(
        response?.data?.reason ||
          "Something went wrong; please try again later...",
      );
    }

    // Handle server errors (502)
    if (response.status === 502) {
      throw new Error(
        "Server is temporarily unavailable. Please try again later.",
      );
    }

    // Handle other error status codes
    throw new Error(
      response?.data?.message || "Failed to update brand properties.",
    );
  } catch (e: any) {
    // Re-throw the error to be handled by the calling function
    throw e;
  }
}

async function postBrandConfigurationProcess(
  oauthTokenHeader: string,
  data: any,
) {
  try {
    const response =
      await brandConfigReportService.postBrandConfigurationReport(
        oauthTokenHeader,
        data,
      );
    if (!response || !response.status) {
      throw new Error("Failed to process brand integration.");
    } else if (response.status === 200) {
      return response;
    } else if (response.status === 400) {
      throw new Error("Bad request - invalid brand configuration data.");
    } else if (response.status === 502) {
      throw new Error("Bad gateway - service temporarily unavailable.");
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText || e?.message || "Failed to process brand integration.",
        type: "error",
      }),
    );
  }
}

export const BrandConfigReportEndPointLogic = {
  getBrandConfigurationReport,
  getBrandConfigPIECharts,
  getBrandBackendRequirements,
  getBrandProperties,
  updateBrandProperties,
  postBrandConfigurationProcess,
};
